import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiError, GenericApiResponse } from 'src/app/interfaces/api';

import {
  Carousel,
  IStrapiDocument,
  LoginRegister,
} from 'src/app/interfaces/strapi';
import { AuthService } from 'src/app/services/auth.service';
import StrapiService from 'src/app/services/strapi.service';
import {
  ICheckIsUnihomeResponse,
  UniService,
} from 'src/app/services/uni.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { NgIf } from '@angular/common';
import { AnalyticsService } from '../../../services/analytics.service';
import { SocialLoginButtonsComponent } from '../../../shared/components/social-login-buttons/social-login-buttons.component';

enum LoginRegisterEnum {
  privateRental = 'i-have-a-private-let-or-rental',
}

@Component({
    selector: 'app-login-register',
    templateUrl: './login-register.component.html',
    styleUrls: ['./login-register.component.scss'],
    imports: [
        NgIf,
        SocialLoginButtonsComponent,
        RouterLink,
    ],
})
export class LoginRegisterComponent extends PageComponent implements OnInit {
  public LoginRegisterEnum = LoginRegisterEnum;

  public override pageData: LoginRegister = <LoginRegister>{};

  public isUnihomeProduct: boolean = false;

  public unihomeBanner: Carousel = {} as Carousel;

  public isError: boolean = false;

  public errorMessage: string = '';

  public universityUUID: string = '';

  public accommodationUUID: string = '';

  private cdr = inject(ChangeDetectorRef);

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    private readonly route: ActivatedRoute,
    private readonly uni: UniService,
    public readonly auth: AuthService,
    private readonly analyticsService: AnalyticsService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    // Clear any social and unihomes local storage data
    this.auth.clearSocialSession();
    this.auth.socialUser = null;

    this.route.queryParams.subscribe((params) => {
      // remove any already existing localstorage data
      localStorage.removeItem('unihomesFlow');
      localStorage.removeItem('universityCmsID');
      localStorage.removeItem('accommodationCmsId');
      // get url params
      const uniUid = params['universityUUID'] as string;
      this.accommodationUUID = params['accommodationUUID'] as string;

      const broadcastId = params['broadcast_id'] as string;

      if (broadcastId) {
        this.analyticsService.trackEvent('registration_broadcast_email', {
          broadcastId,
        });
      }

      if (params['referrer']) this.auth.referrer = String(params['referrer']);

      // Check for university uid
      if (uniUid) {
        this.universityUUID = uniUid;
        localStorage.setItem(
          'universityCmsID',
          params['universityUUID'] as string
        );
        this.doUnihomeCheck(uniUid);
      } else {
        this.setAccommodationUid();
        this.getRegisterPage();
      }

      this.analyticsService.trackEvent('registration_page_landing', {
        universityUUID: this.universityUUID ?? '',
        accommodationUUID: this.accommodationUUID ?? '',
      });
    });

    this.auth.sanctumCSRF().subscribe({
      next: () => {
        this.auth.csrfLoaded = true;
      },
    });
  }

  /**
   * Check if universityUid is a unihomes product.
   * If not then set university uid normally and fetch register page
   * @param uniUid
   */
  public doUnihomeCheck(uniUid: string): void {
    this.uni.checkIsUnihomes(uniUid).subscribe({
      next: (response: GenericApiResponse<ICheckIsUnihomeResponse>) => {
        this.isUnihomeProduct = response.data.is_unihome_product;
        // if unihomes products then set default accommodation and get unihome carousel
        if (this.isUnihomeProduct) {
          this.accommodationUUID = LoginRegisterEnum.privateRental;
          localStorage.setItem('unihomesFlow', 'true');
        }
        this.getRegisterPage();
      },
      error: (e: ApiError) => {
        this.getRegisterPage();
        this.isError = true;
        this.errorMessage =
          e.error?.message ??
          'There was an error checking your university. Please try again';
      },
      complete: () => {
        this.setAccommodationUid();
      },
    });
  }

  /**
   * set accommodationUid in local storage
   * @param accommodationUid
   * @returns
   */
  public setAccommodationUid(): void {
    if (!this.accommodationUUID) return;
    // If we are unihomes default to private let / rental

    localStorage.setItem('accommodationCmsId', this.accommodationUUID);
  }

  /**
   * Get register page from strapi
   * @returns void
   */
  public getRegisterPage(): void {
    this.strapi
      .getRegisterPage()
      .subscribe((data: IStrapiDocument<LoginRegister>) => {
        this.parsePageData(data.data.attributes);
        super.setMetas();
        this.cdr.detectChanges();
      });
  }

  protected override parsePageData(page: LoginRegister): void {
    const theData = structuredClone(page);

    super.parsePageData(theData);

    this.pageData.email_heading = String(theData.email_heading);

    this.pageData.social_heading = String(theData.social_heading);

    this.pageData.login_link_text = String(theData.login_link_text);
  }

  public emailButtonClicked(): void {
    this.auth.clearSocialSession();
    this.analyticsService.trackEvent('email_and_password_start', {
      universityUUID: this.universityUUID ?? '',
      accommodationUUID: this.accommodationUUID ?? '',
      source: 'WEB',
    });
  }
}
